import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Package, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useCart } from '../context/CartContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
    const [searchParams] = useSearchParams();
    const { fetchCart } = useCart();
    const sessionId = searchParams.get('session_id');
    const [status, setStatus] = useState('loading');
    const [paymentInfo, setPaymentInfo] = useState(null);

    useEffect(() => {
        const pollPaymentStatus = async (attempts = 0) => {
            if (!sessionId || attempts >= 5) {
                if (attempts >= 5) setStatus('timeout');
                return;
            }

            try {
                const response = await axios.get(`${API}/api/checkout/status/${sessionId}`, {
                });
                
                setPaymentInfo(response.data);
                
                if (response.data.payment_status === 'paid') {
                    setStatus('success');
                    fetchCart();
                } else if (response.data.status === 'expired') {
                    setStatus('expired');
                } else {
                    setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
                }
            } catch {
                setStatus('error');
            }
        };

        pollPaymentStatus();
    }, [sessionId, fetchCart]);

    if (status === 'loading') {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-16 h-16 animate-spin text-primary mx-auto mb-4" />
                    <h1 className="text-2xl font-bold mb-2">Verifica pagamento in corso...</h1>
                    <p className="text-muted">Attendere prego</p>
                </div>
            </div>
        );
    }

    if (status === 'success') {
        return (
            <div className="min-h-screen py-16">
                <div className="max-w-lg mx-auto px-4 text-center">
                    <div className="bg-surface border border-border rounded-2xl p-8">
                        <CheckCircle className="w-20 h-20 text-success mx-auto mb-6" />
                        <h1 className="text-3xl font-bold mb-4" data-testid="success-title">
                            Ordine confermato!
                        </h1>
                        <p className="text-muted mb-6">
                            Grazie per il tuo acquisto. Riceverai una email di conferma con i dettagli dell'ordine.
                        </p>
                        
                        {paymentInfo && (
                            <div className="bg-surface-elevated rounded-lg p-4 mb-6 text-left">
                                <p className="text-sm text-muted mb-2">Totale pagato</p>
                                <p className="text-2xl font-bold text-primary">
                                    €{(paymentInfo.amount_total / 100).toFixed(2)}
                                </p>
                            </div>
                        )}

                        <div className="space-y-3">
                            <Link to="/ordini" data-testid="view-orders-btn">
                                <Button className="w-full btn-primary">
                                    <Package className="w-4 h-4 mr-2" />
                                    Vedi i tuoi ordini
                                </Button>
                            </Link>
                            <Link to="/store">
                                <Button variant="outline" className="w-full border-border">
                                    Continua lo shopping
                                    <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen py-16">
            <div className="max-w-lg mx-auto px-4 text-center">
                <div className="bg-surface border border-border rounded-2xl p-8">
                    <XCircle className="w-20 h-20 text-danger mx-auto mb-6" />
                    <h1 className="text-3xl font-bold mb-4">
                        {status === 'expired' ? 'Sessione scaduta' : 'Errore nel pagamento'}
                    </h1>
                    <p className="text-muted mb-6">
                        {status === 'expired' 
                            ? 'La sessione di pagamento è scaduta. Riprova.'
                            : 'Si è verificato un errore durante il pagamento. Riprova o contattaci.'}
                    </p>
                    <Link to="/cart">
                        <Button className="btn-primary">
                            Torna al carrello
                        </Button>
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default CheckoutSuccessPage;
